import { useState, useRef, useEffect } from 'react'
import { useNotifications } from '../contexts/NotificationContext'
import axios from 'axios'
import { API_BASE_URL } from '../config/api'
import { usePublicSettings } from './usePublicSettings'

let messageIdCounter = 0

export function useAIChat() {
  const { showSuccess, showError } = useNotifications()
  const settings = usePublicSettings()
  const [messages, setMessages] = useState([])
  const [inputText, setInputText] = useState('')
  
  // ✅ PHASE 1: Initialize threadId from localStorage on mount
  const [threadId, setThreadId] = useState(() => {
    try {
      const saved = localStorage.getItem('ai_last_thread');
      if (saved && saved !== 'null' && saved !== 'undefined') {
        console.log(`💾 [useAIChat] Restored threadId from localStorage: ${saved.substring(0, 8)}...`);
        return saved;
      }
    } catch (error) {
      console.warn('⚠️ [useAIChat] Failed to restore threadId from localStorage:', error);
    }
    return null;
  });
  
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])
  
  // ✅ PHASE 1: Persist threadId to localStorage whenever it changes
  useEffect(() => {
    if (threadId) {
      try {
        localStorage.setItem('ai_last_thread', threadId);
        console.log(`💾 [useAIChat] Saved threadId to localStorage: ${threadId.substring(0, 8)}...`);
      } catch (error) {
        console.warn('⚠️ [useAIChat] Failed to save threadId to localStorage:', error);
      }
    }
  }, [threadId]);

  const addMessage = (message) => {
    setMessages(prev => [...prev, {
      id: message.id || `msg-${Date.now()}-${messageIdCounter++}`, // Use provided ID or generate unique ID
      timestamp: new Date().toISOString(),
      ...message
    }])
  }

  /**
   * ✅ PHASE 1: Set conversation thread ID (from realtime mode)
   */
  const setConversationThread = (id) => {
    console.log(`💬 Setting conversation thread: ${id?.substring(0, 8)}...`);
    setThreadId(id);
  };

  /**
   * ✅ PHASE 1: Extract threadId from existing messages
   * This is useful when switching from realtime to chat
   */
  useEffect(() => {
    if (!threadId && messages.length > 0) {
      // Try to extract threadId from the first message's metadata
      const firstMsg = messages[0];
      if (firstMsg.threadId) {
        console.log(`📝 Extracted threadId from message: ${firstMsg.threadId.substring(0, 8)}...`);
        setThreadId(firstMsg.threadId);
      }
    }
  }, [messages, threadId]);

  const handleSendMessage = async () => {
    if (!inputText.trim()) return

    // Add user message
    addMessage({
      sender: 'user',
      text: inputText,
      type: 'text'
    })

    const userMessage = inputText
    setInputText('')

    // Add "thinking" message
    const thinkingMsgId = `msg-${Date.now()}-${messageIdCounter++}`
    addMessage({
      id: thinkingMsgId,
      sender: 'ai',
      text: '💭 Thinking...',
      type: 'text'
    })

    try {
      // ✅ PHASE 1: Include threadId in request
      console.log(`📤 Sending message with threadId: ${threadId || 'none'}`);
      
      // Call unified chat API (same engine as realtime)
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_BASE_URL}/api/chat/message`, {
        message: userMessage,
        threadId: threadId, // ✅ PHASE 1: Pass threadId for memory persistence
        conversationHistory: messages.map(msg => ({
          sender: msg.sender,
          text: msg.text
        }))
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      // Remove thinking message
      setMessages(prev => prev.filter(m => m.id !== thinkingMsgId))

      // ✅ PHASE 1: Store threadId from response if not already set
      if (response.data.threadId && !threadId) {
        console.log(`💬 Received threadId from response: ${response.data.threadId.substring(0, 8)}...`);
        setThreadId(response.data.threadId);
      }

      // Add AI response
      addMessage({
        sender: 'ai',
        text: response.data.message,
        type: 'text',
        threadId: response.data.threadId || threadId // Store threadId with message
      })

      // If professionals were matched, display them
      if (response.data.professionals && response.data.professionals.length > 0) {
        addMessage({
          sender: 'ai',
          type: 'professionals',
          professionals: response.data.professionals,
          recommendation: response.data.functionCalls?.[0]?.arguments?.severity === 'SEVERE' || 
                         response.data.functionCalls?.[0]?.arguments?.severity === 'MAJOR' 
                         ? 'CONSULTANT' : 'CONTRACTOR',
          criteria: response.data.functionCalls?.[0]?.arguments,
          searchQuery: `${response.data.functionCalls?.[0]?.arguments?.issueType || 'contractor'} ${response.data.functionCalls?.[0]?.arguments?.locationCity || settings?.['market.default_city'] || ''}`.trim(),
          noResults: response.data.professionals.length === 0
        })
      }

      showSuccess('Message sent')
    } catch (error) {
      console.error('Chat message error:', error)
      // Remove thinking message
      setMessages(prev => prev.filter(m => m.id !== thinkingMsgId))
      
      // Add error message
      addMessage({
        sender: 'ai',
        text: 'Sorry, I encountered an error. Please try again.',
        type: 'text'
      })
      
      showError(error.response?.data?.details || 'Failed to send message')
    }
  }

  const handleFileUpload = async () => {
    // Create file input element
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'image/*,video/*'
    fileInput.multiple = true // Enable multiple file selection

    fileInput.onchange = async (e) => {
      const files = Array.from(e.target.files || [])
      if (files.length === 0) return

      // Validate file types
      const invalidFiles = files.filter(file => 
        !file.type.startsWith('image/') && !file.type.startsWith('video/')
      )
      
      if (invalidFiles.length > 0) {
        showError('Please select only image or video files')
        return
      }

      // Convert images to data URLs for thumbnail display
      const fileData = await Promise.all(
        files.map(async (file) => {
          return new Promise((resolve) => {
            const reader = new FileReader()
            reader.onload = (e) => {
              resolve({
                name: file.name,
                dataUrl: e.target.result,
                type: file.type
              })
            }
            reader.readAsDataURL(file)
          })
        })
      )

      // Add user message with image thumbnails
      addMessage({
        sender: 'user',
        text: `📎 ${files.length} image${files.length > 1 ? 's' : ''} uploaded`,
        type: 'images',
        images: fileData
      })

      // Show analyzing message
      const analyzingMsgId = `msg-${Date.now()}-${messageIdCounter++}`
      addMessage({
        id: analyzingMsgId,
        sender: 'ai',
        text: `🔍 Analyzing ${files.length} image${files.length > 1 ? 's' : ''}...`,
        type: 'text'
      })

      try {
        let allAnalyses = []

        // Process each file
        for (const [index, file] of files.entries()) {
          const formData = new FormData()
          formData.append('image', file)
          formData.append('context', `Analyze this home maintenance issue (image ${index + 1} of ${files.length}). What do you see?`)

          const token = localStorage.getItem('token')
          const response = await axios.post(`${API_BASE_URL}/api/realtime/analyze-image`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          })

          if (response.data.success) {
            allAnalyses.push(`**Image ${index + 1}/${files.length} - ${file.name}:**\n${response.data.analysis}`)
          }
        }

        // Remove analyzing message
        setMessages(prev => prev.filter(m => m.id !== analyzingMsgId))
        
        // Add combined AI's analysis
        addMessage({
          sender: 'ai',
          text: allAnalyses.join('\n\n---\n\n'),
          type: 'text'
        })

        showSuccess(`Analyzed ${files.length} image${files.length > 1 ? 's' : ''} successfully`)
      } catch (error) {
        console.error('Image upload error:', error)
        // Remove analyzing message
        setMessages(prev => prev.filter(m => m.id !== analyzingMsgId))
        
        showError(error.response?.data?.details || 'Failed to analyze images')
      }
    }

    // Trigger file picker
    fileInput.click()
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const clearMessages = () => {
    setMessages([])
  }
  
  /**
   * ✅ PHASE 1: Clear threadId and start fresh conversation
   */
  const clearThread = () => {
    try {
      localStorage.removeItem('ai_last_thread');
      setThreadId(null);
      console.log('🧹 [useAIChat] Cleared threadId from state and localStorage');
    } catch (error) {
      console.warn('⚠️ [useAIChat] Failed to clear threadId from localStorage:', error);
    }
  };

  return {
    messages,
    inputText,
    threadId,                    // ✅ PHASE 1: Expose threadId
    conversationId: threadId,    // Alias for backward compatibility
    setInputText,
    messagesEndRef,
    addMessage,
    handleSendMessage,
    handleFileUpload,
    handleKeyPress,
    clearMessages,
    setConversationThread,       // ✅ PHASE 1: Method to set threadId from realtime
    clearThread                  // ✅ PHASE 1: Method to clear threadId
  }
}
